import { Question } from "../../../../../api/src/types";
import config from "../../../config";
import { addDays, isAfter, isBefore, parseISO, startOfDay } from "date-fns";
import { validatePhoneNumber } from "../../../utils/sharedValidators";
import { COLOR_APP_TEXT } from "../../../components/theme";
import styled from "@emotion/styled";
import { Paragraph } from "../../../components/typography/Paragraph";

const WeddingRestrictionContainer = styled.div`
  text-align: left;
  > * {
    margin-bottom: 24px;
  }
`;

const WeddingRestrictionLink = styled.a`
  text-decoration: underline;
  color: ${COLOR_APP_TEXT};
`;

const WeddingRestrictionOL = styled.ol`
  list-style-type: decimal;
  padding-left: 16px;
`;

const WeddingRestrictionLI = styled.li`
  text-align: left;
  margin-bottom: 16px;
`;

const WeddingRestrictionStrong = styled.strong`
  font-weight: 600;
`;

export const validateQuestionnaireDate = (
  date: string,
  question: Question,
  stateAbbreviation?: string,
): string | null => {
  if (
    question.id === config.weddingDateQuestionId &&
    stateAbbreviation === "CA"
  ) {
    const sevenDaysFromToday = addDays(startOfDay(new Date()), 7);
    const selectedDate = parseISO(date);
    if (isBefore(selectedDate, sevenDaysFromToday)) {
      return "In CA you must review the document for 7 days before notarizing";
    }
  }

  if (
    question.id === config.weddingDateQuestionId &&
    stateAbbreviation === "MN"
  ) {
    const sevenDaysFromToday = addDays(startOfDay(new Date()), 7);
    const selectedDate = parseISO(date);
    if (isBefore(selectedDate, sevenDaysFromToday)) {
      return "In MN you must review the document for 7 days before notarizing";
    }
  }

  if (
    question.id === config.weddingDateQuestionId &&
    stateAbbreviation === "NH"
  ) {
    const thirtyDaysFromToday = addDays(startOfDay(new Date()), 30);
    const selectedDate = parseISO(date);
    if (isBefore(selectedDate, thirtyDaysFromToday)) {
      return "In NH you must review the document for 30 days before notarizing";
    }
  }

  if (
    question.id === config.weddingDateQuestionId &&
    stateAbbreviation === "IA"
  ) {
    const thirtyDaysFromToday = addDays(startOfDay(new Date()), 30);
    const selectedDate = parseISO(date);
    if (isBefore(selectedDate, thirtyDaysFromToday)) {
      return "In IA you must review the document for 30 days before notarizing";
    }
  }
  // Check if required field is empty
  if (question.isRequired && (!date || date.trim() === "")) {
    return "Date is required";
  }

  // If empty but not required, allow it
  if (!date || date.trim() === "") {
    return null;
  }

  // Check if it's a valid date
  // parseISO correctly handles YYYY-MM-DD format from HTML date inputs as local dates
  const dateValue = parseISO(date);
  const today = startOfDay(new Date());
  if (isNaN(dateValue.getTime())) {
    return "Please enter a valid date";
  }

  // Apply input restrictions
  if (question.inputRestriction === "FUTURE_DATE") {
    if (isBefore(dateValue, today)) {
      return "Date should be in the future";
    }
  }

  if (question.inputRestriction === "PAST_DATE") {
    if (/birth/i.exec(question.title)) {
      const eighteenYearsAgo = new Date();
      eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);
      const eighteenYearsAgoStart = startOfDay(eighteenYearsAgo);
      if (isAfter(dateValue, eighteenYearsAgoStart)) {
        return "You must be at least 18 years old";
      }
    }

    if (!isBefore(dateValue, today)) {
      return "Date should be in the past";
    }
  }

  return null;
};

export const validateQuestionnaireNumber = (
  value: string,
  question: Question,
): string | null => {
  // Check if required field is empty
  if (question.isRequired && (!value || value.trim() === "")) {
    return "This field is required";
  }

  // If empty but not required, allow it
  if (!value || value.trim() === "") {
    return null;
  }

  // Check if it's a valid number
  if (isNaN(Number(value))) {
    return "Value must be a number";
  }

  // Apply input restrictions
  if (question.inputRestriction === "GREATER_THAN_ZERO") {
    if (Number(value) <= 0) {
      return "Value should be greater than zero";
    }
  }

  return null;
};

export const validateCurrencyWithRestriction = (
  value: string,
  question: Question,
): string | null => {
  // Basic currency validation first
  if (!value) {
    if (question.isRequired) {
      return "Currency value is required";
    }
    return null;
  }

  // Remove currency symbols and parse as number for validation
  const numericValue = parseFloat(value.replace(/[$,]/g, ""));

  if (isNaN(numericValue)) {
    return "Invalid currency value";
  }

  // Apply input restrictions
  if (question.inputRestriction === "GREATER_THAN_ZERO") {
    if (numericValue <= 0) {
      return "Value should be greater than zero";
    }
  }

  return null;
};

export const validatePhoneWithRestriction = (
  value: string,
  question: Question,
): string | null => {
  // Check if required field is empty
  if (question.isRequired && (!value || value.trim() === "")) {
    return "Phone number is required";
  }

  // If empty but not required, allow it
  if (!value || value.trim() === "") {
    return null;
  }

  // Import and use the basic phone validation
  return validatePhoneNumber(value);
};

export const validateTextWithRestriction = (
  value: string,
  question: Question,
): string | null => {
  if (question.isRequired && (!value || value.trim() === "")) {
    return "This field is required";
  }

  // Text fields don't typically use the current input restrictions
  // but we can add basic validation here if needed
  return null;
};

export const errorContainsStateRestriction = (
  error: string | null,
): boolean => {
  return Boolean(error?.match(/^In (CA|MN|NH|IA) /));
};

export const getWeddingRestrictionContent = (
  stateAbbreviation: string,
): { title: string; description: React.ReactElement } => {
  switch (stateAbbreviation) {
    case "CA":
      return {
        title: "It's not you, it's California Law.",
        description: (
          <WeddingRestrictionContainer>
            <Paragraph textAlign="left">
              Your wedding date is within the next 7 days. That means we&apos;re
              legally unable to help you create a prenuptial agreement right now
              due to{" "}
              <WeddingRestrictionLink href="https://app.intercom.com/a/apps/seizs4ey/knowledge-hub/all-content?activeContentId=6338564&activeContentType=article&editorMode=view&search=7%20day%20rule">
                California law&apos;s 7-day rule requirement.
              </WeddingRestrictionLink>
            </Paragraph>
            <Paragraph textAlign="left">
              However, you&apos;ve got two options:
            </Paragraph>
            <WeddingRestrictionOL>
              <WeddingRestrictionLI>
                <Paragraph textAlign="left">
                  <WeddingRestrictionStrong>
                    Move your wedding date back:
                  </WeddingRestrictionStrong>{" "}
                  <br /> If you&apos;re open to pushing your ceremony out just a
                  bit, you&apos;ll still be able to move forward with a prenup.
                </Paragraph>
              </WeddingRestrictionLI>
              <WeddingRestrictionLI>
                <Paragraph textAlign="left">
                  <WeddingRestrictionStrong>
                    Join the Postnup Waitlist:
                  </WeddingRestrictionStrong>{" "}
                  <br /> If rescheduling isn&apos;t an option, no worries. You
                  can create a postnuptial agreement after the wedding instead.
                  It&apos;s similar to a prenup—just done after you say,
                  &lsquo;I do.&rsquo; We know this might not be what you were
                  expecting, but we&apos;re here to help you protect your future
                  the right way.
                </Paragraph>
              </WeddingRestrictionLI>
            </WeddingRestrictionOL>
            <Paragraph textAlign="left">Which would you like to do?</Paragraph>
          </WeddingRestrictionContainer>
        ),
      };
    case "MN":
      return {
        title: "It's not you, it's Minnesota Law.",
        description: (
          <WeddingRestrictionContainer>
            <Paragraph textAlign="left">
              Your wedding date is within the next 7 days. That means we&apos;re
              legally unable to help you create a prenuptial agreement right now
              due to{" "}
              <WeddingRestrictionLink href="https://www.revisor.mn.gov/statutes/cite/519.11">
                Minnesota law&apos;s 7-day rule requirement
              </WeddingRestrictionLink>
              .
            </Paragraph>
            <Paragraph textAlign="left">
              However, you&apos;ve got two options:
            </Paragraph>
            <WeddingRestrictionOL>
              <WeddingRestrictionLI>
                <Paragraph textAlign="left">
                  <WeddingRestrictionStrong>
                    Move your wedding date back:
                  </WeddingRestrictionStrong>{" "}
                  <br /> If you&apos;re open to pushing your ceremony out just a
                  bit, you&apos;ll still be able to move forward with a prenup.
                </Paragraph>
              </WeddingRestrictionLI>
              <WeddingRestrictionLI>
                <Paragraph textAlign="left">
                  <WeddingRestrictionStrong>
                    Join the Postnup Waitlist:
                  </WeddingRestrictionStrong>{" "}
                  <br /> If rescheduling isn&apos;t an option, no worries. You
                  can create a postnuptial agreement after the wedding instead.
                  It&apos;s similar to a prenup—just done after you say,
                  &lsquo;I do.&rsquo; We know this might not be what you were
                  expecting, but we&apos;re here to help you protect your future
                  the right way.
                </Paragraph>
              </WeddingRestrictionLI>
            </WeddingRestrictionOL>
            <Paragraph textAlign="left">Which would you like to do?</Paragraph>
          </WeddingRestrictionContainer>
        ),
      };
    case "NH":
      return {
        title: "It's not you, it's New Hampshire Law.",
        description: (
          <WeddingRestrictionContainer>
            <Paragraph textAlign="left">
              Your wedding is less than 30 days away. In New Hampshire,
              prenuptial agreements are strongly favored when signed at least 30
              days before the wedding. To protect you and your agreement&apos;s
              enforceability, we&apos;re unable to create a prenup on our
              platform at this time.
            </Paragraph>
            <Paragraph textAlign="left">
              However, you&apos;ve got two options:
            </Paragraph>
            <WeddingRestrictionOL>
              <WeddingRestrictionLI>
                <Paragraph textAlign="left">
                  <WeddingRestrictionStrong>
                    Move your wedding date back:
                  </WeddingRestrictionStrong>{" "}
                  <br /> If you&apos;re open to pushing your ceremony out just a
                  bit, you&apos;ll still be able to move forward with a prenup.
                </Paragraph>
              </WeddingRestrictionLI>
              <WeddingRestrictionLI>
                <Paragraph textAlign="left">
                  <WeddingRestrictionStrong>
                    Join the Postnup Waitlist:
                  </WeddingRestrictionStrong>{" "}
                  <br /> If rescheduling isn&apos;t an option, no worries. You
                  can create a postnuptial agreement after the wedding instead.
                  It&apos;s similar to a prenup—just done after you say,
                  &lsquo;I do.&rsquo; We know this might not be what you were
                  expecting, but we&apos;re here to help you protect your future
                  the right way.
                </Paragraph>
              </WeddingRestrictionLI>
            </WeddingRestrictionOL>
            <Paragraph textAlign="left">Which would you like to do?</Paragraph>
          </WeddingRestrictionContainer>
        ),
      };
    case "IA":
      return {
        title: "It's not you, it's Iowa Law.",
        description: (
          <WeddingRestrictionContainer>
            <Paragraph textAlign="left">
              Your wedding is less than 30 days away. In Iowa, prenuptial
              agreements are strongly favored when signed at least 30 days
              before the wedding. To protect you and your agreements&apos;
              enforceability, we&apos;re unable to create a prenup on our
              platform at this time.
            </Paragraph>
            <Paragraph textAlign="left">
              However, you&apos;ve got two options:
            </Paragraph>
            <WeddingRestrictionOL>
              <WeddingRestrictionLI>
                <Paragraph textAlign="left">
                  <WeddingRestrictionStrong>
                    Move your wedding date back:
                  </WeddingRestrictionStrong>{" "}
                  <br /> If you&apos;re open to pushing your ceremony out just a
                  bit, you&apos;ll still be able to move forward with a prenup.
                </Paragraph>
              </WeddingRestrictionLI>
              <WeddingRestrictionLI>
                <Paragraph textAlign="left">
                  <WeddingRestrictionStrong>
                    Join the Postnup Waitlist:
                  </WeddingRestrictionStrong>{" "}
                  <br /> If rescheduling isn&apos;t an option, no worries. You
                  can create a postnuptial agreement after the wedding instead.
                  It&apos;s similar to a prenup—just done after you say,
                  &lsquo;I do.&rsquo; We know this might not be what you were
                  expecting, but we&apos;re here to help you protect your future
                  the right way.
                </Paragraph>
              </WeddingRestrictionLI>
            </WeddingRestrictionOL>
            <Paragraph textAlign="left">Which would you like to do?</Paragraph>
          </WeddingRestrictionContainer>
        ),
      };
    default:
      return {
        title: "",
        description: <></>,
      };
  }
};
